import {makeAutoObservable, observable} from "mobx";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import {DateTime} from "luxon";
import {GAME_TIMEZONE} from "data/constants";
import {ILeaderboardNavigationController} from "views/components/navigations/leaderboard_navigation.controller";
import type {IUserRosterStore} from "data/stores/user_roster/user_roster.store";
import {useNavigate} from "react-router-dom";

interface IParams {
	contestId: number;
	userId: number;
	navigate: ReturnType<typeof useNavigate>;
}

export type ILeaderboardProfileNavigationController = ILeaderboardNavigationController<IParams>;

@injectable()
export class LeaderboardProfileNavigationController
	implements ILeaderboardNavigationController<IParams>
{
	@observable _contestId = 0;
	@observable _userId = 0;
	@observable _navigate: IParams["navigate"] | null = null;

	constructor(
		@inject(Bindings.ContestsStore) private readonly _contestsStore: IContestsStore,
		@inject(Bindings.RoundsStore) private readonly _roundsStore: IRoundsStore,
		@inject(Bindings.ModalsStore) private readonly _modalStore: IModalsStore,
		@inject(Bindings.UserRosterStore) private readonly _userRosterStore: IUserRosterStore
	) {
		makeAutoObservable(this);
	}

	get contestIds(): number[] {
		return this._userRosterStore.contestIds;
	}

	get selectorDate(): string {
		return `${this.contestDay}`;
	}

	get isNextDisabled(): boolean {
		return !this.nextContestId;
	}

	get isPrevDisabled(): boolean {
		return !this.prevContestId;
	}

	private get contestDay(): string {
		const currentContest = this.currentContest;
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("L/dd") : "-";
	}

	public toNext = () => {
		const nextContestId = this.nextContestId;
		if (nextContestId) {
			this.toContestId(nextContestId);
		}
	};

	public toPrev = () => {
		const prevContestId = this.prevContestId;
		if (prevContestId) {
			this.toContestId(prevContestId);
		}
	};

	public openDayPicker = () => {
		this._modalStore.showModal(ModalType.DAY_PICKER, {
			meta: {
				type: "picker",
				lockByContests: this.contestIds,
				onSelect: (contestId: number) => this.toContestId(contestId),
				contestId: this._contestId,
			},
		});
	};

	private get nextContestId() {
		const currentContest = this.currentContest;
		const contests = this.userContests;
		const currentContestIndex = this.userContests.findIndex(
			(it) => it.id === currentContest?.id
		);
		const nextContest = contests[currentContestIndex + 1];
		return nextContest?.id;
	}

	private get prevContestId() {
		const currentContest = this.currentContest;
		const contests = this.userContests;
		const currentContestIndex = this.userContests.findIndex(
			(it) => it.id === currentContest?.id
		);
		const prevContest = contests[currentContestIndex - 1];
		return prevContest?.id;
	}

	private get userContests() {
		const contestsIds = this._userRosterStore.contestIds;

		return this._contestsStore.gameContests.filter((it) => {
			return contestsIds.includes(it.id);
		});
	}

	private get currentContest() {
		return this._contestsStore.getContestById(this._contestId);
	}

	private toContestId(contestId: number) {
		this._navigate?.(`/leaderboards/${this._userId}/${contestId}`);
	}

	init(param: IParams) {
		this._contestId = param.contestId;
		this._userId = param.userId;
		this._navigate = param.navigate;
	}

	onChange(param: IParams) {
		this.init(param);
	}
}
